import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { QueryErrorState } from '@/lib/components';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useAssociations } from '../../hooks/useAssociations';
import type { Pipeline } from '../../hooks/usePipelines';
import { usePipelines } from '../../hooks/usePipelines';
import { usePipelineStages } from '../../hooks/usePipelineStages';
import type { Stage } from '../../hooks/useStages';
import {
  formatCloseDate,
  formatDealAmount,
} from '../../pipeline/utils/deal-card-formatters';
import { formatPipelineLabel } from '../../pipeline/utils/formatPipelineLabel';
import { getStageEntityId } from '../../utils/propertyHelpers';
import { isClosedRiskStatus } from '../../utils/riskStatus';
import { filterActiveDeals } from '../utils/meetingPrepTypes';
import { MeetingPrepAccordionSection } from './meeting-prep-accordion-section';

// ============================================================================
// Module-scope constants
// ============================================================================

const DEAL_PROPERTIES = [
  'ahoy_name',
  'ahoy_amount',
  'ahoy_close_date',
] as const;
const DEAL_OWNER_ASSOCIATION_PROPERTIES = ['ahoy_display_name'] as const;
const DEAL_RISK_ASSOCIATION_PROPERTIES = [
  'ahoy_title',
  'ahoy_severity',
  'ahoy_risk_category',
  'ahoy_status',
] as const;
const DEAL_STAGE_ASSOCIATION_PROPERTIES = ['ahoy_name', 'ahoy_order'] as const;
const PIPELINE_DOT_PAST = 'bg-[var(--color-utility-success-700)]';
const PIPELINE_DOT_CURRENT = 'bg-[var(--color-utility-success-300)]';
const PIPELINE_DOT_DEFAULT = 'bg-[var(--color-border-tertiary)]';

// ============================================================================
// RiskFlag sub-component
// ============================================================================

interface RiskFlagProps {
  title: string;
}

function RiskFlag({ title }: RiskFlagProps) {
  return (
    <div className='flex items-center gap-1.5 px-2 py-1 rounded-md bg-[var(--color-error-50)]'>
      <div className='w-1.5 h-1.5 rounded-full bg-[var(--color-error-700)]' />
      <span className='text-xs text-[var(--color-error-700)]'>{title}</span>
    </div>
  );
}

// ============================================================================
// PipelineDots sub-component
// ============================================================================

interface PipelineDotsProps {
  stages: Stage[];
  currentStageEntityId: string | null;
}

function PipelineDots({ stages, currentStageEntityId }: PipelineDotsProps) {
  const currentStageIndex = useMemo(() => {
    if (!currentStageEntityId) {
      return -1;
    }
    return stages.findIndex(s => s.entity_id === currentStageEntityId);
  }, [stages, currentStageEntityId]);

  return (
    <div>
      <div className='flex gap-1' aria-hidden='true'>
        {stages.map((stage, index) => {
          let dotColor: string;
          if (currentStageIndex === -1) {
            dotColor = PIPELINE_DOT_DEFAULT;
          } else if (index < currentStageIndex) {
            dotColor = PIPELINE_DOT_PAST;
          } else if (index === currentStageIndex) {
            dotColor = PIPELINE_DOT_CURRENT;
          } else {
            dotColor = PIPELINE_DOT_DEFAULT;
          }
          return (
            <div
              key={stage.entity_id}
              className={`h-1 flex-1 rounded-full ${dotColor}`}
            />
          );
        })}
      </div>
      <div className='flex gap-1 mt-0.5'>
        {stages.map((stage, index) => {
          const isCurrent = index === currentStageIndex;
          return (
            <div
              key={stage.entity_id}
              className={`flex-1 text-[10px] truncate ${
                isCurrent
                  ? 'font-medium text-[var(--color-text-secondary)]'
                  : 'text-[var(--color-text-tertiary)]'
              }`}
            >
              {stage.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// DealCard sub-component
// ============================================================================

interface DealCardProps {
  deal: CrmObject;
  getStageByEntityId: (id: string) => Stage | undefined;
  getStagesForObjectType: (type: string) => Stage[];
  stageTypeNames: Set<string>;
  associatedObjects: Record<string, CrmObject>;
  stageIdToPipeline: Map<string, Pipeline>;
}

function DealCard({
  deal,
  getStageByEntityId,
  getStagesForObjectType,
  stageTypeNames,
  associatedObjects,
  stageIdToPipeline,
}: DealCardProps) {
  const dealName = getPropertyValue<string>(deal.properties?.ahoy_name);
  const amount = getPropertyValue<{ amount: number | string; unit: string }>(
    deal.properties?.ahoy_amount
  );
  const closeDate = getPropertyValue<string>(deal.properties?.ahoy_close_date);

  const currentStageEntityId = useMemo(
    () => getStageEntityId(deal, stageTypeNames),
    [deal, stageTypeNames]
  );

  const allStages = useMemo(
    () => getStagesForObjectType('ahoy_deal'),
    [getStagesForObjectType]
  );

  // Resolve the deal's pipeline via its current stage, then restrict dots to
  // that pipeline's stages. Without this, multi-pipeline orgs show every stage
  // from every deal pipeline concatenated into one row.
  const dealPipeline = useMemo(() => {
    if (!currentStageEntityId) {
      return null;
    }
    return stageIdToPipeline.get(currentStageEntityId) ?? null;
  }, [currentStageEntityId, stageIdToPipeline]);

  const pipelineStages = useMemo(() => {
    if (!dealPipeline) {
      return [];
    }
    const stageIds = new Set(
      dealPipeline.stages
        .map(s => s.entity_id)
        .filter((id): id is string => !!id)
    );
    return allStages.filter(s => stageIds.has(s.entity_id));
  }, [allStages, dealPipeline]);

  const pipelineLabel = useMemo(() => {
    if (!dealPipeline) {
      return null;
    }
    return (
      dealPipeline.label || formatPipelineLabel(dealPipeline.pipeline_name)
    );
  }, [dealPipeline]);

  // Extract risks from response-level associated_objects
  const risks = useMemo(() => {
    const riskAssocs =
      deal.associations?.filter(
        a => a.association_type_name === 'ahoy_deal_to_risk'
      ) ?? [];
    return riskAssocs
      .map(a => associatedObjects[a.target_entity_id])
      .filter((r): r is CrmObject => !!r);
  }, [deal, associatedObjects]);

  // Format amount as currency
  const formattedAmount = useMemo(() => formatDealAmount(amount), [amount]);

  // Format close date
  const formattedCloseDate = useMemo(
    () => formatCloseDate(closeDate),
    [closeDate]
  );

  // Compute current stage for display
  const currentStage = useMemo(() => {
    if (!currentStageEntityId) {
      return null;
    }
    return getStageByEntityId(currentStageEntityId) ?? null;
  }, [currentStageEntityId, getStageByEntityId]);

  // Resolve owner display name via ahoy_deal_to_owner association
  const ownerDisplay = useMemo(() => {
    const ownerAssoc = deal.associations?.find(
      a => a.association_type_name === 'ahoy_deal_to_owner'
    );
    if (!ownerAssoc) {
      return null;
    }
    const ownerObj = associatedObjects[ownerAssoc.target_entity_id];
    if (!ownerObj) {
      return null;
    }
    return (
      getPropertyValue<string>(ownerObj.properties?.ahoy_display_name) ?? null
    );
  }, [deal, associatedObjects]);

  return (
    <div className='rounded-lg border border-[var(--color-border-secondary)] p-3 space-y-2'>
      {/* Deal name */}
      {dealName && (
        <p className='text-sm font-semibold text-[var(--color-text-primary)]'>
          {dealName}
        </p>
      )}

      {/* Pipeline dots */}
      {pipelineStages.length > 0 && (
        <div className='space-y-1'>
          {pipelineLabel && (
            <p className='text-caption text-[var(--color-text-tertiary)]'>
              {pipelineLabel}
            </p>
          )}
          <PipelineDots
            stages={pipelineStages}
            currentStageEntityId={currentStageEntityId}
          />
        </div>
      )}

      {/* Deal metadata */}
      <div className='flex flex-wrap gap-x-3 gap-y-1'>
        {formattedAmount && (
          <span className='text-xs text-[var(--color-text-secondary)]'>
            {formattedAmount}
          </span>
        )}
        {formattedCloseDate && (
          <span className='text-xs text-[var(--color-text-tertiary)]'>
            Close {formattedCloseDate}
          </span>
        )}
        {currentStage && (
          <span className='text-xs text-[var(--color-text-tertiary)]'>
            {currentStage.label}
          </span>
        )}
        {ownerDisplay && (
          <span className='text-xs text-[var(--color-text-tertiary)]'>
            {ownerDisplay}
          </span>
        )}
      </div>

      {/* Risk flags */}
      {risks.length > 0 && (
        <div className='flex flex-wrap gap-1'>
          {risks.map(risk => {
            const status = getPropertyValue<string>(
              risk.properties?.ahoy_status
            );
            if (isClosedRiskStatus(status)) {
              return null;
            }
            const title = getPropertyValue<string>(risk.properties?.ahoy_title);
            if (!title) {
              return null;
            }
            return <RiskFlag key={risk.entity_id} title={title} />;
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DealStatusSection main component
// ============================================================================

interface DealStatusSectionProps {
  defaultExpanded: boolean;
  metadata?: string;
  badges?: ReactNode;
  object: CrmObject;
}

export function DealStatusSection({
  defaultExpanded,
  metadata,
  badges,
  object,
}: DealStatusSectionProps) {
  const { currentOrg } = useOrg();
  const orgId = object.org_id;

  const {
    associations: resolvedAssociations,
    isLoading: isAssocLoading,
    error: assocError,
    refetch: refetchAssociations,
  } = useAssociations(currentOrg?.id, object.associations, {
    sourceEntityTypeName: object.entity_type_name,
  });

  const {
    getStageByEntityId,
    getStagesForObjectType,
    stageTypeNames,
    isLoading: isStagesLoading,
    error: stagesError,
    refetch: refetchStages,
  } = usePipelineStages(currentOrg?.id, 'ahoy_deal');

  const {
    pipelines,
    isLoading: isPipelinesLoading,
    error: pipelinesError,
    refetch: refetchPipelines,
  } = usePipelines(currentOrg?.id, 'ahoy_deal');

  const stageIdToPipeline = useMemo(() => {
    const map = new Map<string, Pipeline>();
    for (const pipeline of pipelines) {
      for (const stage of pipeline.stages) {
        if (stage.entity_id) {
          map.set(stage.entity_id, pipeline);
        }
      }
    }
    return map;
  }, [pipelines]);

  // Extract company entity ID from meeting-to-company association.
  //
  // Read `target_entity_id` directly off the raw association rather than the
  // hydrated `object`. `useAssociations` deliberately skips hydration for
  // `ahoy_meeting` sources (see SKIP_ENSURE_FOR_SOURCE_TYPES in
  // useAssociations.ts), so `assoc.object` is null unless the company is
  // already in the store — relying on it here would suppress the deal fetch.
  const companyEntityId = useMemo(() => {
    const companyAssoc = resolvedAssociations.find(
      a => a.association_type_name === 'ahoy_meeting_to_company'
    );
    return companyAssoc?.target_entity_id ?? null;
  }, [resolvedAssociations]);

  // Build deal query options
  const dealQueryOptions = useMemo(
    () =>
      crmObjectListSentinelQuery(
        orgId,
        'ahoy_deal',
        {
          associated_with_entity_id: companyEntityId ?? undefined,
          properties: [...DEAL_PROPERTIES],
        },
        {
          include_associations: {
            ahoy_deal_to_owner: {
              properties: [...DEAL_OWNER_ASSOCIATION_PROPERTIES],
            },
            ahoy_deal_to_risk: {
              properties: [...DEAL_RISK_ASSOCIATION_PROPERTIES],
            },
            ahoy_deal_to_deal_pipeline_stage: {
              properties: [...DEAL_STAGE_ASSOCIATION_PROPERTIES],
            },
          },
        }
      ),
    [orgId, companyEntityId]
  );

  const dealsQuery = useQuery({
    ...dealQueryOptions,
    enabled: !!companyEntityId,
  });
  const list = useStoreBackedList(dealsQuery);

  const associatedObjects = useMemo(
    () => list.associatedObjects,
    [list.associatedObjects]
  );

  // Filter to active (non-terminal) deals — exclude won (probability=1) and lost (probability=0)
  const activeDeals = useMemo(
    () => filterActiveDeals(list.results, getStageByEntityId, stageTypeNames),
    [list.results, getStageByEntityId, stageTypeNames]
  );

  const handleRetry = useCallback(() => {
    void refetchAssociations();
    void refetchStages();
    void refetchPipelines();
    if (companyEntityId) {
      void list.refetch();
    }
  }, [
    refetchAssociations,
    refetchStages,
    refetchPipelines,
    list,
    companyEntityId,
  ]);

  const sectionError =
    assocError ?? list.error ?? stagesError ?? pipelinesError;
  const sectionErrorMessage = sectionError?.message;
  useEffect(() => {
    if (sectionErrorMessage) {
      logger.warn('Failed to load deal status section', {
        component: 'crm_events',
        orgId,
        entityId: object.entity_id,
        error: sectionErrorMessage,
      });
    }
  }, [sectionErrorMessage, orgId, object.entity_id]);

  let content: ReactNode;
  if (
    isAssocLoading ||
    list.isLoading ||
    isStagesLoading ||
    isPipelinesLoading
  ) {
    content = (
      <div className='space-y-2'>
        <div className='h-20 bg-[var(--color-bg-tertiary)] rounded-lg animate-pulse' />
        <div className='h-20 bg-[var(--color-bg-tertiary)] rounded-lg animate-pulse' />
      </div>
    );
  } else if (sectionError) {
    content = (
      <QueryErrorState
        error={sectionError}
        label="We couldn't load deals"
        onRetry={handleRetry}
        size='sm'
      />
    );
  } else if (activeDeals.length === 0) {
    content = (
      <p className='text-sm text-[var(--color-text-tertiary)]'>
        No active deals
      </p>
    );
  } else {
    content = (
      <div className='space-y-2'>
        {activeDeals.map(deal => (
          <DealCard
            key={deal.entity_id}
            deal={deal}
            associatedObjects={associatedObjects}
            getStageByEntityId={getStageByEntityId}
            getStagesForObjectType={getStagesForObjectType}
            stageTypeNames={stageTypeNames}
            stageIdToPipeline={stageIdToPipeline}
          />
        ))}
      </div>
    );
  }

  return (
    <MeetingPrepAccordionSection
      title='Deal status'
      defaultExpanded={defaultExpanded}
      metadata={metadata}
      badges={badges}
      sectionId='deal-status'
    >
      {content}
    </MeetingPrepAccordionSection>
  );
}
